import { LOAN_INFORMATION_TYPES } from '@/app/constants/loanTypes'
import { isAltCurrency } from '@/app/schemas/loan-comparator/loan.schema'
import { IGlobalComparatorOptions, ILoan, ILoanFieldValidation } from '@/app/models/loanQuote'
import { computeLoanMonthsLeft, computeLoanSumLeft } from '@/app/utils/loanCalculations'

// Montant réellement assurable, parité du getter loansTotalAmount du legacy Vue (loan_comparator_module.js) :
// changement d'assurance = restant dû, saisi tel quel ou recalculé depuis le capital initial (différé compris)
export const computeLoansTotalAmount = (
    loans: ILoan[] | undefined | null,
    signatureDate: string | null,
    isSwitching: boolean
): number => {
    if (!loans?.length) return 0

    return loans.reduce((total, loan) => {
        let amount = loan.sum

        if (isSwitching) {
            if (loan.information_type === LOAN_INFORMATION_TYPES.SUM_LEFT) {
                amount = loan.sum_left ?? loan.sum
            } else {
                const monthsLeft = computeLoanMonthsLeft(signatureDate, loan.period)
                amount =
                    monthsLeft !== null && loan.sum
                        ? computeLoanSumLeft(loan.period, loan.sum, loan.rate || 0, loan.time_offset || 0, monthsLeft)
                        : loan.sum
            }
        }

        return total + (amount || 0)
    }, 0)
}

// null tant que le backend n'a pas répondu : sans bornes on ne bloque rien plutôt que de figer le tunnel
export const resolveLoanBounds = (
    globalOptions: IGlobalComparatorOptions | null | undefined,
    loanTypeLabel: string | undefined | null
): ILoanFieldValidation | null => {
    const fieldsValidation = globalOptions?.loanFieldsValidation
    if (!fieldsValidation) return null

    return (loanTypeLabel ? fieldsValidation[loanTypeLabel] : null) ?? fieldsValidation['default'] ?? null
}

// No rate on the front to convert CHF, and only the _alt fields come back from the API before the form mounts
export const hasAltCurrencyLoan = (loans: ILoan[] | undefined | null): boolean =>
    !!loans?.some(
        (loan) =>
            isAltCurrency(loan.selected_currency) ||
            loan.sum_alt_currency != null ||
            loan.sum_left_alt_currency != null
    )

export const getMinCoveragePercentageNeeded = (loansTotal: number, minAmount: number): number => {
    if (!loansTotal || loansTotal <= minAmount) return 100

    return Math.ceil((minAmount / loansTotal) * 100)
}

const formatEuros = (value: number): string => value.toLocaleString('fr-FR')

// The insurers' ceiling, on the capital each borrower covers, shown as soon as it is hit
export const getInsuredAmountError = ({
    loansTotal,
    coverPercentage,
    bounds,
}: {
    loansTotal: number
    coverPercentage: number
    bounds: ILoanFieldValidation['amount'] | null
}): string | null => {
    if (!bounds || loansTotal <= 0) return null

    const insuredAmount = loansTotal * (coverPercentage / 100)

    if (insuredAmount > bounds.max) {
        return `Le montant maximum total assurable est de ${formatEuros(bounds.max)} €.`
    }

    return null
}

// The insurers' floor, kept apart from the ceiling because callers only reveal it once a step change was attempted
export const getMinInsuredAmountError = ({
    loansTotal,
    coverPercentage,
    bounds,
    target,
}: {
    loansTotal: number
    coverPercentage: number
    bounds: ILoanFieldValidation['amount'] | null
    target: 'loan' | 'coverage'
}): string | null => {
    if (!bounds || loansTotal <= 0) return null

    const insuredAmount = loansTotal * (coverPercentage / 100)

    if (insuredAmount >= bounds.min) return null

    if (target === 'coverage') {
        return `Le montant couvert ne peut être inférieur à ${formatEuros(bounds.min)} €, équivalent à un taux de couverture de ${getMinCoveragePercentageNeeded(loansTotal, bounds.min)}%.`
    }

    return `Le montant total de vos prêts ne peut être inférieur à ${formatEuros(bounds.min)} €.`
}
